import axios from 'axios';
import { downloadBinaryFile } from './fileDownload';

const API_ROOT = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');

const MINI_APP_API_ROOT = `${API_ROOT}/telegram-mini-app`;
const SESSION_STORAGE_KEY = 'telegramMiniAppSession';

const readStoredSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const persistSession = (session) => {
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
};

const clearPersistedSession = () => {
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
};

const buildMultipartPayload = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return formData;
};

const miniAppApi = axios.create({
  baseURL: MINI_APP_API_ROOT,
  timeout: 30000
});

miniAppApi.interceptors.request.use((config) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (typeof config.headers?.setContentType === 'function') {
      config.headers.setContentType(undefined);
    } else if (config.headers) {
      delete config.headers['Content-Type'];
    }
  }

  const session = readStoredSession();
  if (session?.token) {
    config.headers.Authorization = `Bearer ${session.token}`;
  }

  return config;
});

miniAppApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearPersistedSession();
    }

    return Promise.reject(error);
  }
);

const telegramMiniAppService = {
  getApiRoot: () => API_ROOT,

  getStoredSession: () => readStoredSession(),

  clearSession: () => clearPersistedSession(),

  authenticate: async (initData) => {
    const response = await miniAppApi.post('/auth', { initData });
    const session = {
      token: response.data.token,
      user: response.data.user,
      telegram: response.data.telegram
    };

    persistSession(session);
    return session;
  },

  getSession: async () => {
    const response = await miniAppApi.get('/session');
    return response.data;
  },

  getMyTickets: async () => {
    const response = await miniAppApi.get('/tickets');
    return response.data.tickets || [];
  },

  getTicketById: async (ticketId) => {
    const response = await miniAppApi.get(`/tickets/${ticketId}`);
    return response.data.ticket;
  },

  acceptTicket: async (ticketId) => {
    const response = await miniAppApi.post(`/tickets/${ticketId}/accept`);
    return response.data;
  },

  startTicket: async (ticketId) => {
    const response = await miniAppApi.post(`/tickets/${ticketId}/start`);
    return response.data;
  },

  updateStatus: async (ticketId, status, comment = '') => {
    const response = await miniAppApi.post(`/tickets/${ticketId}/status`, {
      status,
      comment: comment || undefined
    });
    return response.data;
  },

  addComment: async (ticketId, comment) => {
    const response = await miniAppApi.post(`/tickets/${ticketId}/comment`, { comment });
    return response.data;
  },

  addSolution: async (ticketId, solution) => {
    const response = await miniAppApi.post(`/tickets/${ticketId}/solution`, { solution });
    return response.data;
  },

  uploadAttachment: async (ticketId, file) => {
    const response = await miniAppApi.post(
      `/tickets/${ticketId}/attachment`,
      buildMultipartPayload(file)
    );
    return response.data;
  },

  uploadSolutionAttachment: async (ticketId, file) => {
    const response = await miniAppApi.post(
      `/tickets/${ticketId}/solution-attachment`,
      buildMultipartPayload(file)
    );
    return response.data;
  },

  downloadAttachment: async (ticketId, attachmentId, fileName = null) => {
    return downloadBinaryFile(
      miniAppApi,
      `/tickets/${ticketId}/attachments/${attachmentId}`,
      fileName || `ticket-${ticketId}-attachment-${attachmentId}`
    );
  },

  downloadSolutionAttachment: async (ticketId) => {
    return downloadBinaryFile(
      miniAppApi,
      `/tickets/${ticketId}/solution-attachment`,
      `ticket-${ticketId}-solution-attachment`
    );
  }
};

export default telegramMiniAppService;
